import { create } from "zustand";
import { useEffect } from "react";
import * as employeesService from "@/services/employeesService";
import { getStoresData } from "@/services/storesService";
import { analyticsEvents } from "@/services/analytics";
import { StoreManagementState } from "./storeManagement/types";
import { storeOperations } from "./storeManagement/storeSlice";
import { employeeOperations } from "./storeManagement/employeeSlice";
import { invitationOperations } from "./storeManagement/invitationSlice";
import { setupRealtimeSubscriptions } from "./storeManagement/realtimeSubscriptions";
import { useAuthStore } from "./authStore";
import { supabase } from "@/integrations/supabase/client";
import { fetchUserStores } from "@/services/menu/storeService";
import { Store } from "@/models/storeModel";

// Define FnB industry types
const FNB_INDUSTRIES = ["restaurant", "cafe", "bar", "nightclub", "catering"];

export const useStoreManagementStore = create<StoreManagementState>(
  (set, get) => ({
    stores: getStoresData(),
    employees: employeesService.getEmployeesData(),
    invitations: [],
    loading: false,
    error: null,
    isLoading: false,
    advancedInventoryTracking: false,
    selectedStoreId: localStorage.getItem("selectedStoreId"),
    selectedStore: null,
    isFnBIndustry: false,

    setIsFnBIndustry: (selectedStore: Store | null) => {
      const isFnB = FNB_INDUSTRIES.includes(selectedStore?.industry_type || "");

      set({
        isFnBIndustry: isFnB,
        selectedStore: selectedStore,
      });
    },

    setSelectedStoreId: (storeId: string | null) => {
      set({ selectedStoreId: storeId });

      if (storeId) {
        localStorage.setItem("selectedStoreId", storeId);
        // Update isFnBIndustry when store is selected
        const selectedStore = get().stores.find(
          (store) => store.id === storeId
        );

        const isFnB = selectedStore
          ? FNB_INDUSTRIES.includes(selectedStore.industry_type || "")
          : false;

        set({
          isFnBIndustry: isFnB,
          selectedStore: selectedStore || null,
        });
        // Also persist the selected store data
        if (selectedStore) {
          localStorage.setItem("selectedStore", JSON.stringify(selectedStore));
        }
      } else {
        localStorage.removeItem("selectedStoreId");
        localStorage.removeItem("selectedStore");
        set({
          isFnBIndustry: false,
          selectedStore: null,
        });
      }
    },

    setSelectedStore: (store: Store | null) => {
      set({ selectedStore: store });
      if (store) {
        localStorage.setItem("selectedStore", JSON.stringify(store));
        // Update isFnBIndustry when store is set
        const isFnB = FNB_INDUSTRIES.includes(store.industry_type || "");
        set({ isFnBIndustry: isFnB });
      } else {
        localStorage.removeItem("selectedStore");
        set({ isFnBIndustry: false });
      }
    },

    toggleInventoryTracking: async (storeId: string, enabled: boolean) => {
      set({ loading: true, error: null });
      try {
        const updatedStore = await storeOperations.updateStore(storeId, {
          advanced_inventory_tracking: enabled,
        });
        set({
          stores: get().stores.map((store) =>
            store.id === storeId ? updatedStore : store
          ),
        });
        return Promise.resolve(updatedStore);
      } catch (error) {
        set({ error: "Failed to toggle inventory tracking", loading: false });
        return Promise.reject(error);
      }
    },

    fetchStoresByUserId: async () => {
      const userStores = await fetchUserStores();
      if (userStores.length > 0) {
        set({ stores: userStores });
        set({ selectedStoreId: userStores[0].id });
      }
    },

    // Store operations
    fetchStores: async () => {
      set({ isLoading: true, error: null });
      const user = useAuthStore.getState().user;
      try {
        const { data, error } = await supabase
          .from("stores")
          .select("*")
          .eq("owner_id", user?.id);
        if (error) throw error;

        const storesWithDates = data.map((store: any) => ({
          ...store,
          createdAt: new Date(store.created_at),
          updatedAt: new Date(store.updated_at),
          industry_type: store.industry_type || "other",
          business_hours:
            typeof store.business_hours === "string"
              ? JSON.parse(store.business_hours)
              : store.business_hours || {},
        }));

        set({ stores: storesWithDates });

        // Try to restore the selected store from localStorage
        const savedStoreId = localStorage.getItem("selectedStoreId");
        const savedStore = localStorage.getItem("selectedStore");

        if (savedStoreId && savedStore) {
          const parsedStore = JSON.parse(savedStore);
          // Verify the saved store still exists in the fetched stores
          const storeExists = storesWithDates.some(
            (store) => store.id === savedStoreId
          );
          if (storeExists) {
            set({
              selectedStoreId: savedStoreId,
              selectedStore: parsedStore,
              isFnBIndustry: FNB_INDUSTRIES.includes(
                parsedStore.industryType || ""
              ),
            });
          } else {
            // If saved store no longer exists, select the first store
            if (storesWithDates.length > 0) {
              get().setSelectedStoreId(storesWithDates[0].id);
            }
          }
        } else if (storesWithDates.length > 0) {
          // If no saved store, select the first store
          get().setSelectedStoreId(storesWithDates[0].id);
        }
      } catch (error: any) {
        console.error("Error fetching stores:", error);
        set({ error: error.message });
      } finally {
        set({ isLoading: false });
      }
    },

    addStore: async (storeData) => {
      set({ loading: true, error: null });
      try {
        const newStore = await storeOperations.addStore(storeData);
        const updatedStores = [...get().stores, newStore];
        set({ stores: updatedStores, loading: false });
        return Promise.resolve(newStore);
      } catch (error) {
        set({ error: "Failed to add store", loading: false });
        return Promise.reject(error);
      }
    },

    updateStore: async (id, data) => {
      set({ loading: true, error: null });
      try {
        const updatedStore = await storeOperations.updateStore(id, data);
        const updatedStores = get().stores.map((store) =>
          store.id === id ? updatedStore : store
        );
        set({ stores: updatedStores, loading: false });
        return Promise.resolve(updatedStore);
      } catch (error) {
        set({ error: "Failed to update store", loading: false });
        return Promise.reject(error);
      }
    },

    deleteStore: async (id) => {
      set({ loading: true, error: null });
      try {
        const success = await storeOperations.deleteStore(id);
        if (success) {
          const updatedStores = get().stores.filter((store) => store.id !== id);
          set({ stores: updatedStores, loading: false });
        }
        return Promise.resolve(success);
      } catch (error) {
        set({ error: "Failed to delete store", loading: false });
        return Promise.reject(false);
      }
    },

    fetchEmployees: async (storeId: string) => {
      set({ loading: true });
      try {
        // Build the query
        let query = supabase
          .from("store_employees")
          .select("*")
          .eq("store_id", storeId);

        const { data, error } = await query;

        if (error) throw error;
        // Transform the data to match our model
        const transformedEmployees = data.map((emp: any) => ({
          id: emp.id,
          storeId: emp.store_id,
          userId: emp.user_id,
          firstName: emp.first_name,
          lastName: emp.last_name,
          email: emp.email || "",
          role: emp.role as any,
          address: emp.address || "",
          street: emp.street || "",
          streetNumber: emp.street_number || "",
          city: emp.city || "",
          postcode: emp.postcode || "",
          createdAt: new Date(emp.created_at),
          updatedAt: new Date(emp.updated_at),
        }));
        set({ employees: transformedEmployees, loading: false });
        return transformedEmployees;
      } catch (error: any) {
        console.error("Error fetching employees:", error);
        set({ error: error.message, loading: false });
        return [];
      }
    },

    addEmployee: async (employeeData) => {
      set({ loading: true, error: null });
      try {
        const newEmployee = await employeeOperations.addEmployee(employeeData);

        // After adding an employee, fetch the invitations again
        try {
          await get().fetchInvitations(employeeData.storeId);
        } catch (error) {
          console.error(
            "Failed to refresh invitations after adding employee:",
            error
          );
        }

        set({ loading: false });
        return Promise.resolve(newEmployee);
      } catch (error) {
        set({ error: "Failed to create employee invitation", loading: false });
        return Promise.reject(error);
      }
    },

    updateEmployeeRole: async (id, role) => {
      set({ loading: true, error: null });
      try {
        const updatedEmployee = await employeeOperations.updateEmployeeRole(
          id,
          role
        );
        const updatedEmployees = get().employees.map((emp) =>
          emp.id === id ? updatedEmployee : emp
        );
        set({ employees: updatedEmployees, loading: false });
        return Promise.resolve(updatedEmployee);
      } catch (error) {
        set({ error: "Failed to update employee role", loading: false });
        return Promise.reject(error);
      }
    },

    removeEmployee: async (id) => {
      set({ loading: true, error: null });
      try {
        const success = await employeeOperations.removeEmployee(id);
        if (success) {
          const updatedEmployees = get().employees.filter(
            (emp) => emp.id !== id
          );
          set({ employees: updatedEmployees, loading: false });
        }
        return Promise.resolve(success);
      } catch (error) {
        set({ error: "Failed to remove employee", loading: false });
        return Promise.reject(error);
      }
    },

    // Invitation operations
    fetchInvitations: async (storeId) => {
      set({ loading: true, error: null });
      try {
        const invitations = await invitationOperations.fetchInvitations(
          storeId
        );
        set({ invitations, loading: false });
        return Promise.resolve(invitations);
      } catch (error) {
        set({ error: "Failed to fetch invitations", loading: false });
        return Promise.reject(error);
      }
    },

    revokeInvitation: async (invitationId) => {
      set({ loading: true, error: null });
      try {
        const success = await invitationOperations.revokeInvitation(
          invitationId
        );
        if (success) {
          const updatedInvitations = get().invitations.filter(
            (inv) => inv.id !== invitationId
          );
          set({ invitations: updatedInvitations, loading: false });
        }
        return Promise.resolve(success);
      } catch (error) {
        set({ error: "Failed to revoke invitation", loading: false });
        return Promise.reject(error);
      }
    },

    // Setup realtime subscriptions for employees and invitations
    setupRealtimeSubscriptions: () => {
      return setupRealtimeSubscriptions(
        get().fetchEmployees,
        get().fetchInvitations,
        get().fetchStores
      );
    },
  })
);

// Custom hook to setup realtime subscriptions
export const useRealtimeStoreManagement = () => {
  const setupRealtimeSubscriptions = useStoreManagementStore(
    (state) => state.setupRealtimeSubscriptions
  );

  useEffect(() => {
    const cleanup = setupRealtimeSubscriptions();

    // Cleanup subscriptions when component unmounts
    return cleanup;
  }, [setupRealtimeSubscriptions]);
};
